import { useCallback } from 'react';

import { apiSetABExperimentBucket } from '#/api/abExperiment/apiSetABExperimentBucket';
import type {
  ApiABExperimentBucketForId,
  ApiABExperimentId,
} from '#/api/abExperiment/types/ApiABExperiment';
import { apiFetchUserEnvironment } from '#/api/userEnvironment/apiFetchUserEnvironment';
import type {
  ApiEnvironment,
  ApiUser,
} from '#/api/userEnvironment/types/ApiUserEnvironment';
import { assignAnalyticsAbExperiments } from '#/utils/analytics/abExperiments/analyticsAbExperiments';
import { createZustandContextStore } from '#/zustand-stores/utils/createZustandContextStore';

type UserEnvironmentContextState = {
  user: ApiUser | null;
  environment: ApiEnvironment;
  actionsWhileLoggedOutProcessedAt: number | null;
};

const {
  ZustandContextProvider,
  useZustandContextStore,
  useZustandContextSelector,
} = createZustandContextStore<UserEnvironmentContextState>({
  fallbackState: {
    user: null,
    environment: {
      guessedLocation: {
        text: 'New York, New York, United States',
        countryCode: 'US',
        country: 'United States',
        stateCode: 'NY',
        state: 'New York',
        city: 'New York',
        latitude: 40.7466447,
        longitude: -73.985892,
        timezone: 'America/New_York',
      },
      showAcceptCookies: false,
      isEU: false,
      isUS: false,
      abExperiments: [],
      extraData: {},
    },
    actionsWhileLoggedOutProcessedAt: null,
  },
  onCreate: (store) => {
    store.subscribe((newState) => {
      if (newState.environment) {
        assignAnalyticsAbExperiments(newState.environment.abExperiments);
      }
    });
  },
});

export const UserEnvironmentContextProvider = ZustandContextProvider;

// Hooks

export function useUser(): { user: ApiUser | null } {
  const user = useZustandContextSelector((state) => state.user);
  return { user };
}

export function useLoggedIn(): { loggedIn: boolean } {
  const loggedIn = useZustandContextSelector((state) => Boolean(state.user));
  return { loggedIn };
}

export function useEnvironment() {
  const environment = useZustandContextSelector((state) => state.environment);
  return { environment };
}

export function useActionsWhileLoggedOutProcessedAt() {
  const actionsWhileLoggedOutProcessedAt = useZustandContextSelector(
    (state) => state.actionsWhileLoggedOutProcessedAt,
  );
  return { actionsWhileLoggedOutProcessedAt };
}

export function useUserEnvironmentActions() {
  const store = useZustandContextStore();

  const fetchUserEnvironment = useCallback(async () => {
    const { user, environment } = await apiFetchUserEnvironment();
    store.setState({ user, environment });

    return { user, environment };
  }, [store]);

  const updateUserPartially = useCallback(
    (partialUser: Partial<ApiUser>) => {
      const currentState = store.getState();
      if (currentState.user) {
        store.setState({ user: { ...currentState.user, ...partialUser } });
      }
    },
    [store],
  );

  const updateEnvironmentPartially = useCallback(
    (partialEnvironment: Partial<ApiEnvironment>) => {
      const currentState = store.getState();
      if (currentState.environment) {
        store.setState({
          environment: { ...currentState.environment, ...partialEnvironment },
        });
      }
    },
    [store],
  );

  const setABExperimentBucket = useCallback(
    async <TExperimentId extends ApiABExperimentId>(
      experimentId: TExperimentId,
      bucket: ApiABExperimentBucketForId<TExperimentId>,
    ) => {
      const abExperiments = await apiSetABExperimentBucket(
        experimentId,
        bucket,
      );

      updateEnvironmentPartially({ abExperiments });
    },
    [updateEnvironmentPartially],
  );

  const markActionsWhileLoggedOutProcessed = useCallback(() => {
    store.setState({ actionsWhileLoggedOutProcessedAt: new Date().getTime() });
  }, [store]);

  return {
    setUserEnvironment: store.setState,
    fetchUserEnvironment,
    updateUserPartially,
    updateEnvironmentPartially,
    setABExperimentBucket,
    markActionsWhileLoggedOutProcessed,
  };
}
